import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { getDomainMarketOrigin } from '@libs/search-service-smart-search';
import { getUrlMetadata } from '@libs/feature-toggle';
import { getDomain } from '@libs/utils';
import type { TripOverviewInitiatedMetadata } from '@libs/google-analytics-events';
import { IngredientType } from '@libs/trip-planner-interfaces';

import type {
  ActivityDetailsTicketSelectedMetadata,
  BaseMetadata,
  ChangeHotelEventMetadata,
  CoreEventMetadata,
  EditTripModalOpenedLocationType,
  ExternalErrorQueryParamValue,
  HotelDetailsEventMetadata,
  HotelSelectionFilterAppliedMetadata,
  HotelSelectionFilterClosedMetadata,
  HotelSelectionFilterOpenedMetadata,
  HotelSelectionFilterRemovedMetadata,
  HotelSelectionFilterResetMetadata,
  HotelSelectionInitiatedMetadata,
  HotelSelectionListCardClickedMetaData,
  HotelSelectionSemboBenefitsMetadata,
  MissingCarrierLogoMetadata,
  MissingDestinationInformationMetadata,
  TripOverviewBookClickedMetadata,
  TripOverviewBookClickedWithFarAirportMetadata,
  TripOverviewConfigurationPanelMetadata,
  TripOverviewHeaderActionsMetadata,
  TripOverviewPreferencesModalMetadata,
  TripOverviewPrintModeMetadata,
  TripOverviewUnavailableProductActionClickedMetadata,
  TripOverviewUnavailableProductRenderedMetadata,
} from '../../models';
import { StatsigEventType } from '../../models';
import { useBrowserStore } from '../browserStore';
import { usePreferredTimeStore } from '../preferredTimeStore';
import { useMetaStore } from '../meta/metaStore';
import type { MissingItineraryStaticInfoError } from '../trip/tripStore.model';
import {
  MissingActivitiesStaticInfoError,
  MissingHotelStaticInfoError,
  MissingItineraryAvailabilityError,
  MissingOwnStayStaticInfoError,
  MissingOwnTransportStaticInfoError,
  MissingTransportStaticInfoError,
} from '../trip/tripStore.model';

import { logStatsigEventWrapper } from './logEventFn';

export const useStatsigEventsStore = defineStore('StatsigEvents', () => {
  const browserStore = useBrowserStore();
  const preferredTimeStore = usePreferredTimeStore();
  const metaStore = useMetaStore();

  const baseMetadata = computed<BaseMetadata>(() => {
    const language = getDomain.getLanguageLocale();
    const domain = getDomainMarketOrigin();
    const dateMode = preferredTimeStore.preferredDay
      ? 'selectedDate'
      : 'flexible';
    const isDesktop = browserStore.isSmallerThan1200px ? 'false' : 'true';
    return {
      language,
      domain,
      dateMode,
      isDesktop,
    };
  });

  // State
  const isAlternativeHotelOpened = ref<boolean>(false);

  // Actions: Core
  function logCoreDateChanged(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CoreDateChanged, undefined, { ...metadata });
  }

  function logCorePaxModalOpened(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CorePaxModalOpened, undefined, { ...metadata });
  }

  function logCorePaxUpdated(
    hasSelectedRoom: boolean,
    metadata: CoreEventMetadata
  ): void {
    logEvent(StatsigEventType.CorePaxUpdated, hasSelectedRoom.toString(), {
      ...metadata,
    });
  }

  function logCoreSearchByMovingMapToggle(
    isSearchByMovingMapActive: boolean,
    metadata: CoreEventMetadata
  ): void {
    logEvent(
      StatsigEventType.CoreSearchByMovingMapToggle,
      isSearchByMovingMapActive.toString(),
      { ...metadata }
    );
  }

  function logNoAvailForMonth(isFirstSeach: boolean): void {
    logEvent(StatsigEventType.NoAvailForMonth, isFirstSeach.toString());
  }

  function logCoreNightsSelectorOpened(metadata: CoreEventMetadata): void {
    logEvent(StatsigEventType.CoreNightsSelectorOpened, undefined, {
      ...metadata,
    });
  }

  // Actions: Trip overview
  function logTripOverviewInitiated(
    metadata: TripOverviewInitiatedMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewInitiated, undefined, {
      totalAccomodation: metadata.totalAccomodation.toString(),
      totalFlight: metadata.totalFlight.toString(),
      totalFerry: metadata.totalFerry.toString(),
      totalActivity: metadata.totalActivity.toString(),
    });
  }

  function logTripOverviewAddDestinationClicked(): void {
    logEvent(StatsigEventType.TripOverviewAddDestinationClicked);
  }

  function logTripOverviewBookClicked(
    metadata: TripOverviewBookClickedMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewBookClicked, undefined, {
      totalIngredients: metadata.totalIngredients.toString(),
      totalAccomodation: metadata.totalAccomodation.toString(),
      totalOwnStay: metadata.totalOwnStay.toString(),
      totalFlight: metadata.totalFlight.toString(),
      totalFerry: metadata.totalFerry.toString(),
      totalActivity: metadata.totalActivity.toString(),
      totalAmount: metadata.totalAmount.toString(),
      totalUnbookableTransport: metadata.totalUnbookableTransport.toString(),
      market: metadata.market,
      priceType: metadata.priceType,
    });
  }

  function logTripOverviewBookClickedWithFarAirport(
    metadata: TripOverviewBookClickedWithFarAirportMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewBookClickedWithFarAirport,
      undefined,
      {
        distanceKm: metadata.distanceKm.toString(),
        hotelPolygonId: metadata.hotelPolygonId?.toString() ?? '',
        airportIata: metadata.airportIata,
      }
    );
  }

  function logTripOverviewHotelChangeClicked(
    metadata: ChangeHotelEventMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewHotelChangeClicked, undefined, {
      ...metadata,
    });
  }

  function logTripOverviewTransportSelectorOpened(): void {
    logEvent(StatsigEventType.TripOverviewTransportSelectorOpened);
  }

  function logTripOverviewAccommodationSelectorOpened(): void {
    logEvent(StatsigEventType.TripOverviewAccommodationSelectorOpened);
  }

  function logTripOverviewExploreActivitiesClicked(): void {
    logEvent(StatsigEventType.TripOverviewExploreActivitiesClicked);
  }

  function logTripOverviewActivityBannerElementClicked(): void {
    logEvent(StatsigEventType.TripOverviewActivityBannerElementClicked);
  }

  function logTripOverviewAlternativeAccommodationGalleryImageSwipe(): void {
    logEvent(
      StatsigEventType.TripOverviewAlternativeAccommodationGalleryImageSwipe
    );
  }

  function logTripOverviewAccomodationGalleryImageSwipe(): void {
    logEvent(StatsigEventType.TripOverviewAccomodationGalleryImageSwipe);
  }

  function logTripOverviewActivityGalleryImageSwipe(): void {
    logEvent(StatsigEventType.TripOverviewActivityGalleryImageSwipe);
  }

  function logTripOverviewAlternativeActivityGalleryImageSwipe(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeActivityGalleryImageSwipe);
  }

  function logTripOverviewHotelToggle(value: boolean): void {
    logEvent(StatsigEventType.TripOverviewHotelToggle, value.toString());
  }

  function logTripOverviewOpenFullScreenMap(): void {
    logEvent(StatsigEventType.TripOverviewOpenFullScreenMap);
  }

  function logTripOverviewEditTripModalOpened(
    buttonLocation: EditTripModalOpenedLocationType
  ): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalOpened, buttonLocation);
  }

  function logTripOverviewEditTripModalDestinationAdded(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationAdded);
  }

  function logTripOverviewEditTripModalDestinationChanged(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationChanged);
  }

  function logTripOverviewEditTripModalDestinationRearranged(): void {
    logEvent(StatsigEventType.TripOverviewEditTripModalDestinationRearranged);
  }

  function logTripOverviewFlyFromButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewFlyFromButtonClicked);
  }

  function logTripOverviewFilterButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewFilterButtonClicked);
  }

  function logTripOverviewFiltersApplied(): void {
    logEvent(StatsigEventType.TripOverviewFiltersApplied);
  }

  function logTripOverviewFiltersRemoved(): void {
    logEvent(StatsigEventType.TripOverviewFiltersRemoved);
  }

  function logTripOverviewFiltersReset(): void {
    logEvent(StatsigEventType.TripOverviewFiltersReset);
  }

  function logTripOverviewUnavailableProductActionClicked(
    metadata: TripOverviewUnavailableProductActionClickedMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewUnavailableProductActionClicked,
      undefined,
      metadata
    );
  }

  function logTripOverviewDirectFlightOnlyCheckboxClicked(): void {
    logEvent(StatsigEventType.TripOverviewDirectFlightOnlyCheckboxClicked);
  }

  function logTripOverviewUnavailableProductRendered(
    metadata: TripOverviewUnavailableProductRenderedMetadata
  ): void {
    const metadataWithAffiliateId: TripOverviewUnavailableProductRenderedMetadata & {
      affiliateId: string;
    } = {
      ...metadata,
      affiliateId: metaStore.metaAffiliateId ?? 'noAffiliateId',
    };
    logEvent(
      StatsigEventType.TripOverviewUnavailableProductRendered,
      undefined,
      metadataWithAffiliateId
    );
  }

  function logTripOverviewSeeAlternativeRoomsWhenNoAvail(): void {
    logEvent(StatsigEventType.TripOverviewSeeAlternativeRoomsWhenNoAvail);
  }

  function logTripOverviewSeeAlternativeTicketsWhenNoAvail(): void {
    logEvent(StatsigEventType.TripOverviewSeeAlternativeTicketsWhenNoAvail);
  }

  function logTripOverviewAlternativeStaySelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeStaySelected);
  }

  function logTripOverviewAlternativeActivitySelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeActivitySelected);
  }

  function logTripOverviewAlternativeRoomsDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeRoomsDisplayed);
  }

  function logTripOverviewAlternativeTicketsDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeTicketsDisplayed);
  }

  function logTripOverviewAlternativeFlightDisplayed(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFlightDisplayed);
  }

  function logTripOverviewAlternativeFlightSelected(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeFlightSelected);
  }

  function logTripOverviewHeaderActionClicked(
    metadata: TripOverviewHeaderActionsMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewHeaderActionClicked,
      undefined,
      metadata
    );
  }

  function logTripOverviewContinueWithSelectedDateModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewContinueWithSelectedDateModalOpened);
  }

  function logTripOverviewContinueWithSelectedDateModalConfirmed(): void {
    logEvent(
      StatsigEventType.TripOverviewContinueWithSelectedDateModalConfirmed
    );
  }

  function logTripOverviewContinueWithSelectedDateModalCancelled(): void {
    logEvent(
      StatsigEventType.TripOverviewContinueWithSelectedDateModalCancelled
    );
  }

  function logTripOverviewPriceGuaranteeModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewPriceGuaranteeModalOpened);
  }

  function logTripOverviewEditRoomClicked(): void {
    logEvent(StatsigEventType.TripOverviewEditRoomClicked);
  }

  function logTripOverviewAlternativeHotelsHotelSelectionCardClicked(): void {
    logEvent(
      StatsigEventType.TripOverviewAlternativeHotelsHotelSelectionCardClicked
    );
  }

  function logTripOverviewAlternativeHotelsCardClicked(): void {
    logEvent(StatsigEventType.TripOverviewAlternativeHotelsCardClicked);
  }

  function logTripOverviewPreferencesModalOpened(
    metadata: TripOverviewPreferencesModalMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewPreferencesModalOpened,
      undefined,
      metadata
    );
  }

  function logTripOverviewActivityRemoved(): void {
    logEvent(StatsigEventType.TripOverviewActivityRemoved);
  }

  function logTripOverviewFlightAdded(): void {
    logEvent(StatsigEventType.TripOverviewFlightAdded);
  }

  function logTripOverviewFlightRemoved(): void {
    logEvent(StatsigEventType.TripOverviewFlightRemoved);
  }

  function logTripOverviewFerryAdded(): void {
    logEvent(StatsigEventType.TripOverviewFerryAdded);
  }

  function logTripOverviewFerryRemoved(): void {
    logEvent(StatsigEventType.TripOverviewFerryRemoved);
  }

  function logTripOverviewShareOrSaveButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewShareOrSaveButtonClicked);
  }

  function logTripOverviewShareButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewShareButtonClicked);
  }

  function logTripOverviewSaveButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewSaveButtonClicked);
  }

  function logTripOverviewTripSummaryPanelOpened(): void {
    logEvent(StatsigEventType.TripOverviewTripSummaryPanelOpened);
  }

  function logTripOverviewInstallmentPaymentClicked(): void {
    logEvent(StatsigEventType.TripOverviewInstallmentPaymentClicked);
  }

  function logTripOverviewGlobalSuggestionSwitched(
    globalSuggestionSwitchType: 'cheapest' | 'popular'
  ): void {
    logEvent(StatsigEventType.TripOverviewGlobalSuggestionSwitched, undefined, {
      globalSuggestionSwitchType,
    });
  }

  function logTripOverviewSeeAllHotelsClicked(): void {
    logEvent(StatsigEventType.TripOverviewSeeAllHotelsClicked);
  }

  function logTripOverviewChangeAlternateHotelCardClicked(): void {
    logEvent(StatsigEventType.TripOverviewChangeAlternateHotelCardClicked);
  }

  function logTripOverviewExploreMoreHotelCardClicked(): void {
    logEvent(StatsigEventType.TripOverviewExploreMoreHotelCardClicked);
  }

  function logTripOverviewRemoveDestination(): void {
    logEvent(StatsigEventType.TripOverviewRemoveDestination);
  }

  function logTripOverviewConfigurationPanelOpened(
    metaData: TripOverviewConfigurationPanelMetadata
  ): void {
    logEvent(
      StatsigEventType.TripOverviewConfigurationPanelOpened,
      undefined,
      metaData
    );
  }

  function logTripOverviewReloadPageModalOpened(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageModalOpened);
  }

  function logTripOverviewReloadPageButtonClicked(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageButtonClicked);
  }

  function logTripOverviewReloadPageFailed(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageFailed);
  }

  function logTripOverviewReloadPageSuccess(): void {
    logEvent(StatsigEventType.TripOverviewReloadPageSuccess);
  }

  function logTripOverviewAccommodationUpsellHotelSelectionNavigation(): void {
    logEvent(
      StatsigEventType.TripOverviewAccommodationUpsellHotelSelectionNavigation
    );
  }

  // Actions: Hotel selection
  function logHotelSelectionInitiated(
    metadata: HotelSelectionInitiatedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionInitiated, undefined, {
      placeId: metadata.placeId?.toString() ?? '',
      placeName: metadata.placeName,
      hotelResults: metadata.hotelResults.toString(),
    });
  }

  function logHotelSelectionFilterOpened(
    metadata: HotelSelectionFilterOpenedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterOpened, undefined, {
      polygonId: metadata.polygonId?.toString() ?? '',
      label: metadata.label,
      hotelResults: metadata.hotelResults.toString(),
    });
  }

  function logHotelSelectionFilterClosed(
    metadata: HotelSelectionFilterClosedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterClosed, undefined, {
      polygonId: metadata.polygonId?.toString() ?? '',
      label: metadata.label,
      hotelResults: metadata.hotelResults.toString(),
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterApplied(
    metadata: HotelSelectionFilterAppliedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterApplied, undefined, {
      filterName: metadata.filterName,
      filterGroup: metadata.filterGroup,
      filterDetail: metadata.filterDetail,
      polygonId: metadata.polygonId?.toString() ?? '',
      pathname: metadata.pathname,
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterRemoved(
    metadata: HotelSelectionFilterRemovedMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterRemoved, undefined, {
      filterName: metadata.filterName,
      filterGroup: metadata.filterGroup,
      filterDetail: metadata.filterDetail,
      polygonId: metadata.polygonId?.toString() ?? '',
      pathname: metadata.pathname,
      eventPlacement: metadata.eventPlacement,
    });
  }

  function logHotelSelectionFilterReset(
    metadata: HotelSelectionFilterResetMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionFilterReset, undefined, {
      polygonId: metadata.polygonId?.toString() ?? '',
      label: metadata.label,
    });
  }

  function logHotelSelectionMapMarkerClicked(): void {
    logEvent(StatsigEventType.HotelSelectionMapMarkerClicked);
  }

  function logHotelSelectionListCardClicked(
    metadata: HotelSelectionListCardClickedMetaData
  ): void {
    logEvent(StatsigEventType.HotelSelectionListCardClicked, undefined, {
      isPreviouslySeenHotel: metadata.isPreviouslySeenHotel.toString(),
    });
  }

  function logHotelSelectionMapCardClicked(): void {
    logEvent(StatsigEventType.HotelSelectionMapCardClicked);
  }

  function logHotelSelectionBackButtonClicked(): void {
    logEvent(StatsigEventType.HotelSelectionBackButtonClicked);
  }

  function logHotelSelectionSemboBenefitClicked(
    metadata: HotelSelectionSemboBenefitsMetadata
  ): void {
    logEvent(StatsigEventType.HotelSelectionSemboBenefitClicked, undefined, {
      ...metadata,
    });
  }

  function logHotelSelectionPeekPriceModalOpened(): void {
    logEvent(StatsigEventType.HotelSelectionPeekPriceModalOpened);
  }

  function logHotelSelectionPeekPriceModalDateSelected(): void {
    logEvent(StatsigEventType.HotelSelectionPeekPriceModalDateSelected);
  }

  function logHotelSelectionPeekPriceModalMonthSelected(): void {
    logEvent(StatsigEventType.HotelSelectionPeekPriceModalMonthSelected);
  }

  function logHotelCardClickedWithRating(): void {
    logEvent(StatsigEventType.HotelCardClickedWithRating);
  }

  // Actions: Hotel details
  function logHotelDetailsInitiated(metadata: HotelDetailsEventMetadata): void {
    logEvent(StatsigEventType.HotelDetailsInitiated, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsReviewOpened(): void {
    logEvent(StatsigEventType.HotelDetailsReviewOpened);
  }

  function logHotelDetailsSelected(metadata: HotelDetailsEventMetadata): void {
    logEvent(StatsigEventType.HotelDetailsSelected, undefined, { ...metadata });
  }

  function logHotelDetailsStaySelected(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsStaySelected, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsFullDescriptionModalOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(
      StatsigEventType.HotelDetailsFullDescriptionModalOpened,
      undefined,
      { ...metadata }
    );
  }

  function logHotelDetailsAmenitiesModalOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsAmenitiesModalOpened, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsShowStaysButtonClicked(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsShowStaysButtonClicked, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsRoomGalleryOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsRoomGalleryOpened, undefined, {
      ...metadata,
    });
  }

  function logHotelDetailsPriceGuaranteeModalOpened(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(
      StatsigEventType.HotelDetailsPriceGuaranteeModalOpened,
      undefined,
      {
        ...metadata,
      }
    );
  }

  function logHotelDetailsContinueWithSelectedRooms(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(
      StatsigEventType.HotelDetailsContinueWithSelectedRooms,
      undefined,
      {
        ...metadata,
      }
    );
  }

  function logHotelDetailsWithoutAvailActionTaken(
    metadata: HotelDetailsEventMetadata
  ): void {
    logEvent(StatsigEventType.HotelDetailsWithoutAvailActionTaken, undefined, {
      ...metadata,
    });
  }

  // Actions: Activity Selection
  function logActivitySelectionInitiated(): void {
    logEvent(StatsigEventType.ActivitySelectionInitiated);
  }

  function logActivitySelectionGalleryImageSwipe(): void {
    logEvent(StatsigEventType.ActivitySelectionGalleryImageSwipe);
  }

  function logActivitySelectionFilterOpened(): void {
    logEvent(StatsigEventType.ActivitySelectionFilterOpened);
  }

  // Actions: Activity details
  function logActivityDetailsInitiated(): void {
    logEvent(StatsigEventType.ActivityDetailsInitiated);
  }

  function logActivityDetailsTicketSelected(
    metadata: ActivityDetailsTicketSelectedMetadata
  ): void {
    logEvent(StatsigEventType.ActivityDetailsTicketSelected, undefined, {
      productCode: metadata.productCode,
    });
  }

  function logActivityDetailsOpenTicketsSelector(): void {
    logEvent(StatsigEventType.ActivityDetailsOpenTicketsSelector);
  }

  // Actions: Flights List
  function logFlightsListInitiated(): void {
    logEvent(StatsigEventType.FlightListInitiated);
  }

  function logFlightsListProductSelected(): void {
    logEvent(StatsigEventType.FlightListProductSelected);
  }

  function logFlightsListProductDeselected(): void {
    logEvent(StatsigEventType.FlightListProductDeselected);
  }

  function logFlightsListChangeAirportModalOpened(): void {
    logEvent(StatsigEventType.FlightListChangeAirportModalOpened);
  }

  function logFlightsListAirportChanged(): void {
    logEvent(StatsigEventType.FlightListAirportChanged);
  }

  function logFlightsListFilterModalOpened(): void {
    logEvent(StatsigEventType.FlightListFilterModalOpened);
  }

  function logFlightsListFilterApplied(): void {
    logEvent(StatsigEventType.FlightListFilterApplied);
  }

  // Actions: Ferry List
  function logFerryListInitiated(): void {
    logEvent(StatsigEventType.FerryListInitiated);
  }

  function logFerryListProductSelected(): void {
    logEvent(StatsigEventType.FerryListProductSelected);
  }

  // Actions: External errors
  function logExternalErrorDisplayed(
    externalErrorType: ExternalErrorQueryParamValue
  ): void {
    logEvent(StatsigEventType.ExternalErrorDisplayed, undefined, {
      externalErrorType,
    });
  }

  function logBookingFailedErrorTryAgain(): void {
    logEvent(StatsigEventType.BookingFailedErrorTryAgain);
  }

  function logBookingFailedErrorBuildNewTrip(): void {
    logEvent(StatsigEventType.BookingFailedErrorBuildNewTrip);
  }

  function logTransportStaticInfoNotAvailable(missingCarrier: string): void {
    logEvent(StatsigEventType.TransportStaticInfoNotAvailable, missingCarrier);
  }

  // Actions: Telemetry
  function logMissingAmenityConfiguration(unknownAmenityName: string): void {
    logEvent(StatsigEventType.MissingAmenityConfiguration, unknownAmenityName);
  }

  function logTripOverviewInfiniteLoad(): void {
    logEvent(StatsigEventType.TripOverviewInfiniteLoad, undefined, {
      ...getUrlMetadata(),
    });
  }

  function logTripOverviewInfiniteLoadReasons(
    itineraryError:
      | MissingItineraryAvailabilityError
      | MissingItineraryStaticInfoError
  ): void {
    if (itineraryError instanceof MissingItineraryAvailabilityError) {
      logEvent(
        StatsigEventType.TripOverviewInfiniteLoadMissingAvailability,
        undefined,
        {
          ...getUrlMetadata(),
        }
      );
    } else {
      itineraryError.ingredientErrors.forEach((error) => {
        if (error instanceof MissingOwnStayStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.OwnStay,
              ...getUrlMetadata(),
            }
          );
        } else if (error instanceof MissingHotelStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.Hotel,
              date: error.date.toISOString(),
              code: error.code ?? '',
              availability: error.availability ?? '',
              ...getUrlMetadata(),
            }
          );
        } else if (error instanceof MissingTransportStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.Transport,
              transportType: error.transportType,
              date: error.date.toISOString(),
              availability: error.availability,
              transportIds: error.transportIds.join(','),
              ...getUrlMetadata(),
            }
          );
        } else if (error instanceof MissingActivitiesStaticInfoError) {
          error.activitiesWithMissingStaticInfo.forEach((activityError) => {
            logEvent(
              StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
              undefined,
              {
                ingredientType: IngredientType.Activity,
                availability: activityError.availability,
                code: activityError.code,
                date: activityError.date.toISOString(),
                ...getUrlMetadata(),
              }
            );
          });
        } else if (error instanceof MissingOwnTransportStaticInfoError) {
          logEvent(
            StatsigEventType.TripOverviewInfiniteLoadMissingStaticInfo,
            undefined,
            {
              ingredientType: IngredientType.OwnTransport,
              ...getUrlMetadata(),
            }
          );
        }
      });
    }
  }

  function logHotelSelectionInfiniteLoad(): void {
    logEvent(StatsigEventType.HotelSelectionInfiniteLoad, undefined, {
      ...getUrlMetadata(),
    });
  }

  function logMissingCarrierLogo(metadata: MissingCarrierLogoMetadata): void {
    logEvent(StatsigEventType.MissingCarrierLogo, undefined, {
      ...metadata,
    });
  }

  function logMissingDestinationInformation(
    metadata: MissingDestinationInformationMetadata
  ): void {
    logEvent(StatsigEventType.MissingDestinationInformation, undefined, {
      ...metadata,
      polygonId: metadata.polygonId.toString(),
    });
  }

  function logHotelSelectionWithWrongIndexAccessed(currentUrl: string): void {
    logEvent(StatsigEventType.HotelSelectionWithWrongIndexAccessed, undefined, {
      currentUrl,
    });
  }

  function logHotelSelectionWithOldIndexAccessed(currentUrl: string): void {
    logEvent(StatsigEventType.HotelSelectionWithOldIndexAccessed, undefined, {
      currentUrl,
    });
  }

  function logActivitySelectionWithWrongIndexAccessed(): void {
    logEvent(StatsigEventType.ActivitySelectionWithWrongIndexAccessed);
  }

  function logActivitySelectionWithOldIndexAccessed(): void {
    logEvent(StatsigEventType.ActivitySelectionWithOldIndexAccessed);
  }

  function logEvent(
    eventName: string,
    eventValue?: string | number | undefined,
    eventMetadata?: Record<string, string> | undefined
  ): void {
    logStatsigEventWrapper(
      eventName,
      eventValue,
      eventMetadata,
      baseMetadata.value
    );
  }

  function logCarPlannerInitiated(): void {
    logEvent(StatsigEventType.CarPlannerInitialized);
  }

  function logCarPlannerShowSuggestedTrip(): void {
    logEvent(StatsigEventType.CarPlannerShowSuggestedTrip);
  }

  // Action: Print mode
  function logTripOverviewPrintModeOpened(
    metadata: TripOverviewPrintModeMetadata
  ): void {
    logEvent(StatsigEventType.TripOverviewPrintModeOpened, undefined, {
      source: metadata.source,
    });
  }

  return {
    // State
    isAlternativeHotelOpened,

    // Getters
    baseMetadata,

    // Actions: Core
    logCoreDateChanged,
    logCorePaxModalOpened,
    logCorePaxUpdated,
    logCoreSearchByMovingMapToggle,
    logNoAvailForMonth,
    logCoreNightsSelectorOpened,

    // Actions: Trip overview
    logTripOverviewInitiated,
    logTripOverviewAccommodationSelectorOpened,
    logTripOverviewAddDestinationClicked,
    logTripOverviewBookClicked,
    logTripOverviewBookClickedWithFarAirport,
    logTripOverviewHotelChangeClicked,
    logTripOverviewTransportSelectorOpened,
    logTripOverviewExploreActivitiesClicked,
    logTripOverviewActivityBannerElementClicked,
    logTripOverviewHotelToggle,
    logTripOverviewOpenFullScreenMap,
    logTripOverviewEditTripModalOpened,
    logTripOverviewEditTripModalDestinationAdded,
    logTripOverviewEditTripModalDestinationChanged,
    logTripOverviewEditTripModalDestinationRearranged,
    logTripOverviewFlyFromButtonClicked,
    logTripOverviewFilterButtonClicked,
    logTripOverviewFiltersApplied,
    logTripOverviewFiltersRemoved,
    logTripOverviewFiltersReset,
    logTripOverviewUnavailableProductActionClicked,
    logTripOverviewUnavailableProductRendered,
    logTripOverviewAlternativeStaySelected,
    logTripOverviewAlternativeActivitySelected,
    logTripOverviewSeeAlternativeRoomsWhenNoAvail,
    logTripOverviewSeeAlternativeTicketsWhenNoAvail,
    logTripOverviewAlternativeRoomsDisplayed,
    logTripOverviewAlternativeTicketsDisplayed,
    logTripOverviewAlternativeFlightDisplayed,
    logTripOverviewAlternativeFlightSelected,
    logTripOverviewHeaderActionClicked,
    logTripOverviewAlternativeAccommodationGalleryImageSwipe,
    logTripOverviewAccomodationGalleryImageSwipe,
    logTripOverviewActivityGalleryImageSwipe,
    logTripOverviewAlternativeActivityGalleryImageSwipe,
    logTripOverviewContinueWithSelectedDateModalOpened,
    logTripOverviewContinueWithSelectedDateModalConfirmed,
    logTripOverviewContinueWithSelectedDateModalCancelled,
    logTripOverviewPriceGuaranteeModalOpened,
    logTripOverviewEditRoomClicked,
    logTripOverviewAlternativeHotelsHotelSelectionCardClicked,
    logTripOverviewAlternativeHotelsCardClicked,
    logTripOverviewPreferencesModalOpened,
    logTripOverviewActivityRemoved,
    logTripOverviewFlightAdded,
    logTripOverviewFlightRemoved,
    logTripOverviewFerryAdded,
    logTripOverviewFerryRemoved,
    logTripOverviewTripSummaryPanelOpened,
    logTripOverviewShareOrSaveButtonClicked,
    logTripOverviewShareButtonClicked,
    logTripOverviewSaveButtonClicked,
    logTripOverviewInstallmentPaymentClicked,
    logTripOverviewDirectFlightOnlyCheckboxClicked,
    logTripOverviewGlobalSuggestionSwitched,
    logTripOverviewSeeAllHotelsClicked,
    logTripOverviewChangeAlternateHotelCardClicked,
    logTripOverviewExploreMoreHotelCardClicked,
    logTripOverviewRemoveDestination,
    logTripOverviewConfigurationPanelOpened,
    logTripOverviewReloadPageModalOpened,
    logTripOverviewReloadPageButtonClicked,
    logTripOverviewReloadPageFailed,
    logTripOverviewReloadPageSuccess,
    logTripOverviewAccommodationUpsellHotelSelectionNavigation,

    // Actions: Hotel selection
    logHotelSelectionInitiated,
    logHotelSelectionFilterOpened,
    logHotelSelectionFilterClosed,
    logHotelSelectionFilterApplied,
    logHotelSelectionFilterRemoved,
    logHotelSelectionFilterReset,
    logHotelSelectionMapMarkerClicked,
    logHotelSelectionListCardClicked,
    logHotelSelectionMapCardClicked,
    logHotelSelectionBackButtonClicked,
    logHotelSelectionSemboBenefitClicked,
    logHotelSelectionPeekPriceModalOpened,
    logHotelSelectionPeekPriceModalDateSelected,
    logHotelSelectionPeekPriceModalMonthSelected,
    logHotelCardClickedWithRating,

    // Actions: Hotel details
    logHotelDetailsInitiated,
    logHotelDetailsReviewOpened,
    logHotelDetailsSelected,
    logHotelDetailsStaySelected,
    logHotelDetailsFullDescriptionModalOpened,
    logHotelDetailsAmenitiesModalOpened,
    logHotelDetailsShowStaysButtonClicked,
    logHotelDetailsRoomGalleryOpened,
    logHotelDetailsPriceGuaranteeModalOpened,
    logHotelDetailsContinueWithSelectedRooms,
    logHotelDetailsWithoutAvailActionTaken,

    // Actions: Activity Selection
    logActivitySelectionInitiated,
    logActivitySelectionGalleryImageSwipe,
    logActivitySelectionFilterOpened,

    // Actions: Activity details
    logActivityDetailsInitiated,
    logActivityDetailsTicketSelected,
    logActivityDetailsOpenTicketsSelector,

    // Actions: Flights List
    logFlightsListInitiated,
    logFlightsListProductSelected,
    logFlightsListProductDeselected,
    logFlightsListChangeAirportModalOpened,
    logFlightsListAirportChanged,
    logFlightsListFilterModalOpened,
    logFlightsListFilterApplied,

    // Actions: Ferry List
    logFerryListInitiated,
    logFerryListProductSelected,

    // Actions: External errors
    logExternalErrorDisplayed,
    logBookingFailedErrorTryAgain,
    logBookingFailedErrorBuildNewTrip,
    logTransportStaticInfoNotAvailable,

    // Actions: Telemetry
    logMissingAmenityConfiguration,
    logTripOverviewInfiniteLoad,
    logTripOverviewInfiniteLoadReasons,
    logHotelSelectionInfiniteLoad,
    logMissingCarrierLogo,
    logMissingDestinationInformation,
    logHotelSelectionWithWrongIndexAccessed,
    logHotelSelectionWithOldIndexAccessed,
    logActivitySelectionWithWrongIndexAccessed,
    logActivitySelectionWithOldIndexAccessed,

    // Actions: Car planner
    logCarPlannerInitiated,
    logCarPlannerShowSuggestedTrip,

    // Actions: Print mode
    logTripOverviewPrintModeOpened,
  };
});
